import { useEffect, useState } from 'react'
import { Network, AlertTriangle, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Host {
  host: string
  ip: string | null
  ports: number[]
  vulns: string[]
  tags: string[]
  cpes: string[]
  risky: { port: number; service: string }[]
}

/** Censys-style port exposure via Shodan InternetDB (free, no scan).
 * Resolves discovered subdomains → IP → Shodan lookup. Surfaces risky
 * open ports + reported CVEs without us doing any port scanning. */
export default function PortExposure() {
  const [data, setData] = useState<{ hosts: Host[]; summary: any } | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchAll = () => {
    setLoading(true)
    api.get('/api/cyber/port-exposure', { timeout: 90_000 })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }

  useEffect(fetchAll, [])

  if (loading) return <div className="card p-6 text-center text-text-muted text-sm">Querying Shodan InternetDB for monitored hosts… (this can take 60s)</div>
  if (!data) return null

  const s = data.summary || {}
  const riskyHosts = data.hosts.filter(h => h.risky?.length > 0 || h.vulns?.length > 0)

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Network size={14} className="text-cyan" /> External port exposure
          <span className="text-[10px] text-text-muted font-normal">
            via Shodan InternetDB · {s.total_hosts || 0} hosts · {s.with_risky || 0} risky · {s.with_vulns || 0} with reported CVEs
          </span>
        </h2>
        <button type="button" onClick={fetchAll} className="btn-ghost text-xs">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {riskyHosts.length === 0 ? (
        <p className="text-[11px] text-emerald-light italic">No risky open ports or known CVEs reported by Shodan for monitored hosts.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Host</th>
                <th className="text-left py-2 px-2">IP</th>
                <th className="text-left py-2 px-2">Risky open ports</th>
                <th className="text-left py-2 px-2">CVEs reported</th>
              </tr>
            </thead>
            <tbody>
              {riskyHosts.slice(0, 30).map(h => (
                <tr key={h.host} className="border-b border-border/50 hover:bg-surface-hover">
                  <td className="py-1.5 px-2 font-medium text-text-primary">{h.host}</td>
                  <td className="py-1.5 px-2 text-text-muted font-mono">{h.ip || '—'}</td>
                  <td className="py-1.5 px-2">
                    <div className="flex flex-wrap gap-1">
                      {h.risky.map(r => (
                        <span key={r.port}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose/15 text-rose-light border border-rose/40 text-[9px] font-mono"
                          title={r.service}>
                          <AlertTriangle size={8} /> {r.port}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-1.5 px-2">
                    <div className="flex flex-wrap gap-1">
                      {h.vulns.slice(0, 4).map(v => (
                        <span key={v} className="font-mono text-[9px] text-rose hover:underline">{v}</span>
                      ))}
                      {h.vulns.length > 4 && <span className="text-[9px] text-text-muted">+{h.vulns.length - 4}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[9px] text-text-muted mt-2 italic">
        Data sourced from Shodan InternetDB (free, no scan from us). Risky-port set covers SSH/RDP/SMB/MongoDB/Redis/Elasticsearch/etc.
      </p>
    </div>
  )
}
